import React from 'react';
import { RaceConfidence } from '../../types';
import './ConfidenceBand.css';

interface Props {
  data: RaceConfidence | null;
  loading: boolean;
  goalTime?: string;
}

function parseGoalSeconds(formatted: string): number | null {
  const parts = formatted.split(':').map(Number);
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return null;
}

const ConfidenceBand: React.FC<Props> = ({ data, loading, goalTime }) => {
  if (loading || !data || data.insufficient_data) return null;

  const { percentiles, goal_probability, median_formatted } = data;
  if (!percentiles || percentiles.length < 2) return null;

  const p10 = percentiles[0];
  const p100 = percentiles[percentiles.length - 1];
  const p50 = percentiles.find(p => p.p === 50)!;

  const goalSeconds = goalTime ? parseGoalSeconds(goalTime) : null;
  const minT = p10.time_seconds;
  const maxT = p100.time_seconds;
  const range = maxT - minT || 1;

  const goalX = goalSeconds
    ? Math.max(0, Math.min(100, ((goalSeconds - minT) / range) * 100))
    : null;
  const medianX = ((p50.time_seconds - minT) / range) * 100;

  const probColor =
    (goal_probability || 0) >= 40 ? '#22c55e' :
    (goal_probability || 0) >= 20 ? '#eab308' : '#ef4444';

  return (
    <div className="cb-inline">
      <div className="cb-inline-header">
        <span className="cb-inline-label">Predicted finish</span>
        {goal_probability !== undefined && goalTime && (
          <span className="cb-inline-prob" style={{ color: probColor }}>
            {goal_probability}% goal chance
          </span>
        )}
      </div>

      {/* thin gradient bar */}
      <div className="cb-bar-wrap">
        <div className="cb-bar">
          {/* median tick */}
          <div className="cb-tick cb-tick--median" style={{ left: `${medianX}%` }} />
          {/* goal tick */}
          {goalX !== null && goalSeconds! >= minT && goalSeconds! <= maxT && (
            <div className="cb-tick cb-tick--goal" style={{ left: `${goalX}%` }} />
          )}
        </div>
      </div>

      <div className="cb-range-labels">
        <span>{p10.time_formatted}</span>
        <span className="cb-median-label">↑ {median_formatted}</span>
        <span>{p100.time_formatted}</span>
      </div>
    </div>
  );
};

export default ConfidenceBand;
